import { useState, useMemo } from "react";
import { getErrorMessage } from "@/lib/errors";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, ChevronRight, ChevronLeft, Check, Plus, Trash2, Bitcoin, Coins, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrencies } from "@/hooks/useUserPreferences";
import { ALL_CURRENCIES, CRYPTO_CURRENCIES } from "@/hooks/useExchangeRates";
import { 
  parseLocaleNumber, 
  getDecimalSeparator, 
  isValidLocaleNumber, 
  formatLocaleNumber,
  getLocalePlaceholder 
} from "@/hooks/useLocale";

interface WalletSetup {
  name: string;
  currency: string;
  balance: string;
  manualRate: string;
}

interface AccountSetup {
  name: string;
  type: string;
  initialBalance: string;
  currency: string;
  manualRate: string;
  wallets: WalletSetup[];
}

const ACCOUNT_TYPES = [
  { value: "bank", label: "Bank Account" },
  { value: "credit", label: "Credit Card" },
  { value: "cash", label: "Cash" },
  { value: "investment", label: "Investment" },
  { value: "savings", label: "Savings" },
  { value: "virtual", label: "Virtual/Digital" },
  { value: "crypto", label: "Crypto Wallet" },
];

// Common locale options
const LOCALE_OPTIONS = [
  { code: "en-US", label: "English (US)", example: "1,234.56" },
  { code: "en-GB", label: "English (UK)", example: "1,234.56" },
  { code: "de-DE", label: "Deutsch", example: "1.234,56" },
  { code: "fr-FR", label: "Français", example: "1 234,56" },
  { code: "es-ES", label: "Español", example: "1.234,56" },
  { code: "it-IT", label: "Italiano", example: "1.234,56" },
  { code: "pt-BR", label: "Português (BR)", example: "1.234,56" },
  { code: "nl-NL", label: "Nederlands", example: "1.234,56" },
  { code: "ja-JP", label: "日本語", example: "1,234.56" },
  { code: "zh-CN", label: "中文", example: "1,234.56" },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currencies = useCurrencies();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Detect user's locale from browser
  const detectedLocale = navigator.language || "en-US";
  const [locale, setLocale] = useState(detectedLocale);
  
  const decimalSeparator = useMemo(() => getDecimalSeparator(locale), [locale]);
  const placeholder = useMemo(() => getLocalePlaceholder(locale), [locale]);
  
  const [currency, setCurrency] = useState("USD");
  const [accounts, setAccounts] = useState<AccountSetup[]>([
    { name: "", type: "bank", initialBalance: "", currency: "USD", manualRate: "", wallets: [] }
  ]);

  const addAccount = () => {
    setAccounts([...accounts, { name: "", type: "bank", initialBalance: "", currency, manualRate: "", wallets: [] }]);
  };

  const removeAccount = (index: number) => {
    if (accounts.length > 1) {
      setAccounts(accounts.filter((_, i) => i !== index));
    }
  };

  const updateAccount = (index: number, field: keyof Omit<AccountSetup, 'wallets'>, value: string) => {
    const updated = [...accounts];
    (updated[index] as any)[field] = value;
    setAccounts(updated);
  };

  const addWallet = (accountIndex: number) => {
    const updated = [...accounts];
    updated[accountIndex].wallets.push({ 
      name: "", 
      currency: currency, 
      balance: "", 
      manualRate: "" 
    });
    setAccounts(updated);
  };

  const removeWallet = (accountIndex: number, walletIndex: number) => {
    const updated = [...accounts];
    updated[accountIndex].wallets = updated[accountIndex].wallets.filter((_, i) => i !== walletIndex);
    setAccounts(updated);
  };

  const updateWallet = (accountIndex: number, walletIndex: number, field: keyof WalletSetup, value: string) => {
    const updated = [...accounts];
    updated[accountIndex].wallets[walletIndex][field] = value;
    setAccounts(updated);
  };

  // Helper to format parsed value for preview
  const getValidationPreview = (value: string, currencyCode: string) => {
    if (!value || value.trim() === "") return null;
    const isValid = isValidLocaleNumber(value);
    const parsed = parseLocaleNumber(value);
    const symbol = getAccountCurrencySymbol(currencyCode);
    return { isValid, parsed, formatted: `${symbol}${formatLocaleNumber(parsed, locale)}` };
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Update currency, locale preference and mark onboarding complete
      const { error: prefError } = await supabase
        .from("user_preferences")
        .upsert({
          user_id: user.id,
          currency,
          locale,
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString()
        }, { onConflict: "user_id" });

      if (prefError) throw prefError;

      // Create accounts with initial balances and currency
      const validAccounts = accounts.filter(a => a.name.trim());
      if (validAccounts.length > 0) {
        // Delete existing accounts and wallets to avoid duplicates on re-onboarding
        await supabase.from("wallets").delete().eq("user_id", user.id);
        await supabase.from("accounts").delete().eq("user_id", user.id);

        const { data: createdAccounts, error: accountError } = await supabase
          .from("accounts")
          .insert(validAccounts.map(a => ({
            user_id: user.id,
            name: a.name.trim(),
            account_type: a.type,
            initial_balance: parseLocaleNumber(a.initialBalance),
            currency: a.currency,
            manual_exchange_rate: a.manualRate ? parseLocaleNumber(a.manualRate) : null,
          })))
          .select();

        if (accountError) throw accountError;

        // Create wallets for each account
        if (createdAccounts) {
          const walletsToCreate: any[] = [];
          
          // Match accounts by name since order isn't guaranteed
          validAccounts.forEach((account) => {
            const createdAccount = createdAccounts.find(
              ca => ca.name === account.name.trim()
            );
            if (createdAccount && account.wallets.length > 0) {
              account.wallets.forEach(wallet => {
                if (wallet.name.trim()) {
                  walletsToCreate.push({
                    user_id: user.id,
                    account_id: createdAccount.id,
                    name: wallet.name.trim(),
                    currency: wallet.currency,
                    balance: parseLocaleNumber(wallet.balance),
                    manual_exchange_rate: wallet.manualRate ? parseLocaleNumber(wallet.manualRate) : null,
                  });
                }
              });
            }
          });

          if (walletsToCreate.length > 0) {
            const { error: walletError } = await supabase
              .from("wallets")
              .insert(walletsToCreate);
            
            if (walletError) throw walletError;
          }
        }
      }

      toast({ title: "Welcome!", description: "Your accounts have been set up successfully." });
      navigate("/app");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast({ title: "Error", description: `Failed to complete setup: ${getErrorMessage(error)}`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Set default preferences and mark onboarding complete
      await supabase
        .from("user_preferences")
        .upsert({ 
          user_id: user.id, 
          currency: "USD",
          locale: detectedLocale,
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString()
        }, { onConflict: "user_id" });

      navigate("/app");
    } catch (error) {
      console.error("Skip onboarding error:", error);
      toast({ title: "Error", description: "Failed to skip setup", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const currencySymbol = currencies.find(c => c.code === currency)?.symbol || "$";
  
  const getAccountCurrencySymbol = (code: string) => {
    const curr = ALL_CURRENCIES.find(c => c.code === code);
    return curr?.symbol || "$";
  };

  const isCrypto = (code: string) => CRYPTO_CURRENCIES.some(c => c.code === code);
  
  const getTotalWallets = () => accounts.reduce((sum, a) => sum + a.wallets.filter(w => w.name.trim()).length, 0);

  const steps = [
    // Step 0: Welcome
    <motion.div
      key="welcome"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="text-center space-y-6"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/60 text-primary-foreground mb-4">
        <DollarSign className="h-10 w-10" />
      </div>
      <h2 className="text-3xl font-bold">Welcome to Budge AI!</h2>
      <p className="text-muted-foreground max-w-md mx-auto">
        Let's get you set up in just a few steps. We'll help you configure your currency preference and add your financial accounts.
      </p>
    </motion.div>,

    // Step 1: Locale/Region
    <motion.div
      key="locale"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Number Format</h2>
        <p className="text-muted-foreground">Select how you write numbers in your region</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
        {LOCALE_OPTIONS.map((loc) => (
          <button
            key={loc.code}
            onClick={() => setLocale(loc.code)}
            className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
              locale === loc.code
                ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                : "border-border hover:border-primary/50 hover:bg-muted/50"
            }`}
          >
            <div className="font-medium">{loc.label}</div>
            <div className="text-sm text-muted-foreground font-mono">{loc.example}</div>
          </button>
        ))}
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Detected from browser: <span className="font-mono">{detectedLocale}</span>
      </p>
    </motion.div>,

    // Step 2: Currency
    <motion.div
      key="currency"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Choose Your Currency</h2>
        <p className="text-muted-foreground">Select the currency you use most often</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg mx-auto">
        {currencies.map((curr) => (
          <button
            key={curr.code}
            onClick={() => setCurrency(curr.code)}
            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
              currency === curr.code
                ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                : "border-border hover:border-primary/50 hover:bg-muted/50"
            }`}
          >
            <div className="text-2xl font-mono font-bold">{curr.symbol}</div>
            <div className="text-sm text-muted-foreground">{curr.code}</div>
          </button>
        ))}
      </div>
    </motion.div>,

    // Step 2: Accounts
    <motion.div
      key="accounts"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold">Add Your Accounts</h2>
        <p className="text-muted-foreground">Set up your financial accounts with their current balances</p>
      </div>
      <div className="space-y-4 max-w-lg mx-auto">
        {accounts.map((account, index) => (
          <Card key={index} className="relative overflow-hidden gradient-border-top">
            <CardContent className="pt-6 space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor={`name-${index}`}>Account Name</Label>
                  <Input
                    id={`name-${index}`}
                    placeholder="e.g., Main Checking"
                    value={account.name}
                    onChange={(e) => updateAccount(index, "name", e.target.value)}
                  />
                </div>
                {accounts.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="mt-6 text-muted-foreground hover:text-destructive"
                    onClick={() => removeAccount(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Account Type</Label>
                  <Select 
                    value={account.type} 
                    onValueChange={(v) => {
                      updateAccount(index, "type", v);
                      // Auto-set currency for crypto type
                      if (v === "crypto" && !isCrypto(account.currency)) {
                        updateAccount(index, "currency", "BTC");
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            {type.value === "crypto" && <Bitcoin className="h-3 w-3" />}
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Account Currency</Label>
                  <Select 
                    value={account.currency} 
                    onValueChange={(v) => updateAccount(index, "currency", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {account.type === "crypto" ? (
                        CRYPTO_CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} {curr.code} - {curr.name}
                          </SelectItem>
                        ))
                      ) : (
                        ALL_CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} {curr.code} - {curr.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`balance-${index}`}>Current Balance</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">
                      {getAccountCurrencySymbol(account.currency)}
                    </span>
                    <Input
                      id={`balance-${index}`}
                      type="text"
                      inputMode="decimal"
                      placeholder={placeholder}
                      className={`pl-8 font-mono ${
                        account.initialBalance && !isValidLocaleNumber(account.initialBalance) 
                          ? "border-destructive focus-visible:ring-destructive" 
                          : ""
                      }`}
                      value={account.initialBalance}
                      onChange={(e) => updateAccount(index, "initialBalance", e.target.value)}
                    />
                  </div>
                  {account.initialBalance && (
                    <div className="mt-1">
                      {(() => {
                        const preview = getValidationPreview(account.initialBalance, account.currency);
                        if (!preview) return null;
                        return preview.isValid ? (
                          <p className="text-xs text-primary-text flex items-center gap-1">
                            <Check className="h-3 w-3" /> Parsed as: {preview.formatted}
                          </p>
                        ) : (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Invalid number format
                          </p>
                        );
                      })()}
                    </div>
                  )}
                  {!account.initialBalance && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Use "{decimalSeparator}" for decimals
                    </p>
                  )}
                </div>
                {account.currency !== currency && (
                  <div>
                    <Label htmlFor={`rate-${index}`}>
                      Manual Rate <span className="text-xs text-muted-foreground">(optional)</span>
                    </Label>
                    <Input
                      id={`rate-${index}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="Auto-fetch"
                      className="font-mono"
                      value={account.manualRate}
                      onChange={(e) => updateAccount(index, "manualRate", e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      1 {account.currency} = ? {currency}
                    </p>
                  </div>
                )}
              </div>

              {/* Wallets Section */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Coins className="h-4 w-4" />
                    Currency Wallets <span className="text-xs">(optional)</span>
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => addWallet(index)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Wallet
                  </Button>
                </div>
                
                {account.wallets.length > 0 ? (
                  <div className="space-y-3">
                    {account.wallets.map((wallet, walletIndex) => (
                      <div 
                        key={walletIndex} 
                        className="p-3 rounded-lg bg-muted/30 border border-border/50 space-y-3"
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Input
                              placeholder="Wallet name (e.g., EUR Balance)"
                              value={wallet.name}
                              onChange={(e) => updateWallet(index, walletIndex, "name", e.target.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeWallet(index, walletIndex)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Select 
                            value={wallet.currency} 
                            onValueChange={(v) => updateWallet(index, walletIndex, "currency", v)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ALL_CURRENCIES.map((curr) => (
                                <SelectItem key={curr.code} value={curr.code}>
                                  {curr.symbol} {curr.code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">
                              {getAccountCurrencySymbol(wallet.currency)}
                            </span>
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder={decimalSeparator === "," ? "0,00" : "0.00"}
                              className="h-8 text-sm pl-6 font-mono"
                              value={wallet.balance}
                              onChange={(e) => updateWallet(index, walletIndex, "balance", e.target.value)}
                            />
                          </div>
                        </div>
                        {wallet.currency !== currency && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground">1 {wallet.currency} =</span>
                            <Input
                              type="number"
                              step="0.000001"
                              placeholder="Auto"
                              className="h-6 text-xs font-mono w-20"
                              value={wallet.manualRate}
                              onChange={(e) => updateWallet(index, walletIndex, "manualRate", e.target.value)}
                            />
                            <span className="text-muted-foreground">{currency}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground/60 italic">
                    Add wallets for multi-currency accounts (e.g., Revolut with USD, EUR, BTC)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={addAccount}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Another Account
        </Button>
      </div>
    </motion.div>,

    // Step 3: Done
    <motion.div
      key="done"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="text-center space-y-6"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/60 text-white mb-4">
        <Check className="h-10 w-10" />
      </div>
      <h2 className="text-3xl font-bold">You're All Set!</h2>
      <p className="text-muted-foreground max-w-md mx-auto">
        Your accounts are ready. Start tracking your finances and take control of your money.
      </p>
      <div className="pt-4 space-y-2">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50">
          <span className="text-muted-foreground">Currency:</span>
          <span className="font-mono font-bold">{currencySymbol} {currency}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          {accounts.filter(a => a.name.trim()).length} account(s) configured
          {getTotalWallets() > 0 && ` • ${getTotalWallets()} wallet(s)`}
        </div>
      </div>
    </motion.div>,
  ];

  const canProceed = () => {
    // Step 3 is Accounts - require at least one account name
    if (step === 3) {
      return accounts.some(a => a.name.trim());
    }
    return true;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center gap-2 mb-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step
                    ? "w-8 bg-primary"
                    : i < step
                    ? "w-2 bg-primary/60"
                    : "w-2 bg-muted"
                }`}
              />
            ))}
          </div>
          <CardDescription>Step {step + 1} of 5</CardDescription>
        </CardHeader>
        <CardContent className="min-h-[400px] flex flex-col">
          <div className="flex-1 flex items-center justify-center py-6">
            <AnimatePresence mode="wait">
              {steps[step]}
            </AnimatePresence>
          </div>
          <div className="flex justify-between items-center pt-6 border-t">
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              disabled={step === 0 || loading}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-2">
              {step < 4 && (
                <Button 
                  variant="ghost" 
                  onClick={handleSkip}
                  disabled={loading}
                  className="text-muted-foreground"
                >
                  {loading ? "Skipping..." : "Skip for now"}
                </Button>
              )}
              {step < 4 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleComplete} 
                  disabled={loading}
                  className="bg-gradient-to-r from-primary to-primary/60 hover:opacity-90"
                >
                  {loading ? "Setting up..." : "Get Started"}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;
